import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, Phone } from "lucide-react";
import SEO from "../components/SEO";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 page-enter">
      <SEO
        title="Privacy Policy — Arbe Minimart"
        description="How Arbe Minimart collects, uses and protects your personal data under the Philippine Data Privacy Act of 2012 (RA 10173)."
        path="/privacy"
      />

      <div className="overline">Legal</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1C2826] tracking-tighter">Privacy Policy</h1>
      <p className="mt-2 text-sm text-[#738380]">Last updated: April 2026</p>

      <div className="mt-6 bg-[#F1F3EE] border border-[#E1E5DF] rounded-2xl p-5 flex items-start gap-3">
        <Shield className="h-5 w-5 text-[#2F5233] mt-0.5 flex-shrink-0" />
        <div className="text-sm leading-relaxed">
          Arbe Minimart respects your privacy and complies with the <span className="font-semibold">Philippine Data Privacy Act of 2012 (Republic Act No. 10173)</span>. This page explains what personal information we collect, why we collect it, and how we keep it safe.
        </div>
      </div>

      <section className="mt-8 prose prose-sm max-w-none text-[#1C2826]">
        <h2 className="font-heading text-xl font-bold mt-8 mb-2">1. Who we are</h2>
        <p>This website (<a href="https://arbeminimart.com" className="text-[#2F5233] underline">arbeminimart.com</a>) and the related online store, POS and delivery service are operated by <strong>Arbe Minimart</strong>, with main office at Militante Street D'ban, Don Bernardo Neri Poblacion, Calamba, Misamis Occidental, 7210, Philippines, and Branch 1 at Felix Taghap Street, Poblacion 2, Oroquieta City, Misamis Occidental, 7207, Philippines.</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">2. Information we collect</h2>
        <p>We only collect information that is necessary to serve you:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account information</strong> — full name, email address, mobile number, and an encrypted password when you register.</li>
          <li><strong>Order &amp; delivery details</strong> — delivery address, contact number, order items, payment method, optional delivery notes, and uploaded payment proof images.</li>
          <li><strong>Transaction records</strong> — order totals, dates, and the assigned driver.</li>
          <li><strong>Technical information</strong> — basic browser, device, and access logs used to keep the service secure and reliable.</li>
        </ul>
        <p className="mt-2">We <strong>do not</strong> store full credit/debit card numbers, GCash PINs, Maya PINs, or bank passwords on our servers.</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">3. How we use your information</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>To process your orders, accept payments, and arrange delivery to your address.</li>
          <li>To send you the 4-digit delivery code that confirms the package is handed to the right person.</li>
          <li>To contact you about your order if there is a stock issue, delivery delay, or address concern.</li>
          <li>To improve our store, products, and customer experience.</li>
          <li>To comply with applicable Philippine laws (e.g., BIR record-keeping).</li>
        </ul>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">4. Who we share your information with</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Our drivers</strong> — receive only your name, phone, address and order items, so they can deliver your purchase. Drivers <em>cannot</em> see your delivery code; you share it with them at your door.</li>
          <li><strong>Payment providers</strong> (e.g. GCash, Maya, your bank, PayPal) — only the data they require to process the transaction.</li>
          <li><strong>Legal authorities</strong> — when required by a valid Philippine court order, subpoena, or law.</li>
        </ul>
        <p className="mt-2">We <strong>never sell</strong> your personal information to third parties.</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">5. How long we keep your data</h2>
        <p>Account and order records are retained while your account is active and for at least 5 years after your last transaction, in line with BIR record-keeping requirements. You may request deletion at any time (see Section 7).</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">6. How we protect your data</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Passwords are stored using industry-standard one-way hashing (bcrypt). We can never see your password.</li>
          <li>All traffic between your browser and our servers is encrypted via HTTPS.</li>
          <li>Access to admin and staff features is restricted by role and protected with JWT authentication.</li>
        </ul>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">7. Your rights under RA 10173</h2>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Be informed of what data we hold about you.</li>
          <li>Access, correct or update your personal information.</li>
          <li>Request deletion of your account and associated data (subject to BIR retention rules).</li>
          <li>Object to processing or withdraw consent at any time.</li>
          <li>File a complaint with the National Privacy Commission (NPC) at <a href="https://privacy.gov.ph" className="text-[#2F5233] underline" target="_blank" rel="noopener noreferrer">privacy.gov.ph</a>.</li>
        </ul>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">8. Cookies &amp; local storage</h2>
        <p>We use a small amount of browser storage (localStorage) to keep you signed in, remember your shopping cart, and remember whether you've dismissed our welcome popup. We do not use third-party advertising trackers.</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">9. Children's privacy</h2>
        <p>Arbe Minimart is not directed to children under 13. We do not knowingly collect data from minors. If you believe a child has registered, please contact us so we can delete the account.</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">10. Updates to this policy</h2>
        <p>We may update this Privacy Policy from time to time. The "Last updated" date at the top will reflect any changes. Continued use of the site after an update means you accept the revised policy.</p>

        <h2 className="font-heading text-xl font-bold mt-8 mb-2">11. Contact our Data Privacy Officer</h2>
        <p>For any privacy concerns, requests, or to exercise your rights:</p>
        <div className="not-prose mt-3 p-4 bg-white border border-[#E1E5DF] rounded-xl text-sm space-y-2">
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#C62828]" /> <a href="tel:079202971717" className="text-[#2F5233] hover:underline">079202971717</a> / <a href="tel:09202799298" className="text-[#2F5233] hover:underline">09202799298</a></div>
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#C62828]" /> <a href="mailto:privacy@arbeminimart.com" className="text-[#2F5233] hover:underline">privacy@arbeminimart.com</a></div>
        </div>
      </section>

      <div className="mt-10 text-sm">
        See also: <Link to="/terms" className="text-[#2F5233] underline font-semibold">Terms of Service</Link>
      </div>
    </div>
  );
}
